import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { UserManagementService } from 'src/app/features/user-management/services/user-management.service';
import { SessionService } from '../services/session/session.service';
import { SsoScopeService } from '../services/sso/sso-scope.service';
import { StorageBrowser } from '../services/storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private session: SessionService,
    private storageBrowser: StorageBrowser,
    private userManagementService: UserManagementService,
    private ssoScope: SsoScopeService,
  ) {}

  initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      if (!token) {
        console.log('No token found, skipping user initialization');
        resolve(); // No token, skip user initialization
        return;
      }

      // Only treat token=... as an AUTH JWT if it looks like a JWT (3 dot-separated parts)
      const isJwt = !!token && token.split('.').length === 3;
      if (!isJwt) {
        // e.g. password reset token is a random string -> ignore here

        resolve();
        return;
      }

      // Decode the JWT and log the payload
      const decodedToken = decodeJwt(token);
      const userId = decodedToken?.userId;
      if (!userId) {
        console.error('Invalid token: userId not found');
        resolve();
        return;
      }
      this.storageBrowser.set('token', token);
      this.userManagementService.getUserById(userId).subscribe({
        next: (res) => {
          console.log('User initialized:', res);
          this.storageBrowser.set('user', res);
          resolve();
        },
        error: (err) => {
          console.error('Error initializing user:', err);
          reject(err);
        },
      });
    });
  }

  public canActivate(route: ActivatedRouteSnapshot): boolean {
    const token = this.storageBrowser.get('token');
    if (!token) {
      this.router.navigate(['/auth/login']);
      return false;
    }
    if (this.ssoScope.isActive()) return true
    const valid = this.session.isSessionValid();
    return valid;
  }

}

function decodeJwt(token: string): any {
  try {
    const payload = token.split('.')[1]; // Extract the payload part of the JWT
    const decodedPayload = atob(payload); // Decode the Base64-encoded payload
    return JSON.parse(decodedPayload); // Parse the JSON payload
  } catch (error) {
    console.error('Invalid JWT token:', error);
    return null;
  }
}
